import { Link } from 'react-router-dom';
import SiteLayout from '@/components/feature/SiteLayout';
import Breadcrumb from '@/components/base/Breadcrumb';
import SectionHeading from '@/components/base/SectionHeading';
import usePageMeta from '@/hooks/usePageMeta';

const principles = [
  {
    icon: 'ri-shield-check-line',
    title: '不把传闻当结论',
    desc: '尚未核实的来源会明确标注状态，读者一眼能看出哪些已确认、哪些仍待观察。',
  },
  {
    icon: 'ri-time-line',
    title: '用时间线讲清楚',
    desc: '同一件事的进展按发生顺序排列，避免读者被碎片信息带偏。',
  },
  {
    icon: 'ri-scales-3-line',
    title: '尽量呈现多方视角',
    desc: '涉及不同立场的议题，我们会尽量呈现多个角度，而不是只给一个答案。',
  },
  {
    icon: 'ri-refresh-line',
    title: '错了就改',
    desc: '内容有误时我们会在原位置更新说明，而不是悄悄替换。',
  },
];

const values = [
  { num: '01', title: '内容原则', desc: '真实、可追溯、有上下文，是这个站点所有内容的前置条件。' },
  { num: '02', title: '更新节奏', desc: '工作日每日更新，热点事件当天补充进展，不做无意义的堆量。' },
  { num: '03', title: '读者第一', desc: '所有排版与功能设计，都是为了让内容更容易被读完、被理解。' },
];

const jsonLd = [
  {
    '@context': 'https://schema.org',
    '@type': 'AboutPage',
    name: '关于看料99',
    url: 'https://lfthg.cn/about',
  },
  {
    '@context': 'https://schema.org',
    '@type': 'Organization',
    name: '看料99',
    email: 'hello@kanliao99.com',
    description: '看料99 是专注独家爆料与首发资源的资讯门户。',
  },
];

export default function About() {
  usePageMeta({
    title: '关于我们 - 看料99 专注独家爆料与首发资源',
    description:
      '了解看料99 的品牌定位、内容原则与发展理念。我们以时间线还原事件脉络，标注信息状态，为读者提供可追溯、有上下文的第一手消息。',
    keywords: '看料99,关于看料99,看料99独家内幕,看料99第一手消息,看料99内容原则',
    path: '/about',
    jsonLd,
  });

  return (
    <SiteLayout>
      <div className="animate-fade-up">
        <section className="border-b border-background-200 bg-background-100">
          <div className="mx-auto w-full max-w-[1280px] px-4 py-8 md:px-6 md:py-12">
            <Breadcrumb items={[{ label: '首页', to: '/' }, { label: '关于我们' }]} />
            <h1 className="mt-4 font-heading text-3xl font-bold text-foreground-950 md:text-4xl">
              关于 看料99
            </h1>
            <p className="mt-3 max-w-3xl text-sm leading-relaxed text-foreground-600 md:text-base">
              看料99 是一个专注独家爆料与首发资源的资讯门户。我们不追求最快，但追求每条消息都能被读懂。
            </p>
          </div>
        </section>

        <section aria-label="品牌介绍" className="mx-auto w-full max-w-[1280px] px-4 py-12 md:px-6 md:py-16">
          <div className="grid grid-cols-1 items-center gap-10 lg:grid-cols-[1.1fr_1fr]">
            <div>
              <SectionHeading
                eyebrow="品牌介绍"
                title="把散落的信息，重新排成一条时间线"
                desc="娱乐八卦、社会热点、职场规则、生活变化——这些话题每天都在刷屏，但真正被讲清楚的并不多。看料99 做的事很具体：把同一件事的不同片段按时间顺序整理好，标注信息来源的状态，再把背景补齐。"
              />
              <p className="mt-4 text-sm leading-relaxed text-foreground-600">
                我们面向的是愿意多花两分钟了解来龙去脉的读者。你不需要被标题牵着走，也不需要在一堆重复内容里自己拼图。打开一篇文章，就能顺着脉络把它读完。
              </p>
              <div className="mt-6 flex flex-wrap gap-3">
                <Link
                  to="/category"
                  className="inline-flex items-center gap-1.5 rounded-md bg-primary-500 px-4 py-2.5 text-sm font-medium text-background-50 dark:text-foreground-950 whitespace-nowrap transition-colors hover:bg-primary-600"
                >
                  浏览全部分类
                  <i className="ri-arrow-right-line" aria-hidden="true"></i>
                </Link>
                <a
                  href="#contact"
                  className="inline-flex items-center gap-1.5 rounded-md border border-background-300 px-4 py-2.5 text-sm font-medium text-foreground-800 whitespace-nowrap transition-colors hover:border-primary-300 hover:text-primary-600"
                >
                  <i className="ri-mail-line" aria-hidden="true"></i>
                  联系我们
                </a>
              </div>
            </div>
            <div className="h-[320px] w-full overflow-hidden rounded-lg md:h-[400px]">
              <img
                src="https://readdy.ai/api/search-image?query=editorial%20desk%20with%20open%20laptop%20timeline%20notes%20pinned%20on%20a%20cork%20board%2C%20warm%20amber%20desk%20lamp%2C%20cozy%20journalism%20studio%2C%20terracotta%20and%20cream%20tones%2C%20calm%20documentary%20photography&width=1000&height=900&seq=kanliao-about-14&orientation=portrait"
                alt="看料99 编辑整理时间线的工作场景"
                title="看料99 内容整理"
                className="h-full w-full object-cover object-top"
              />
            </div>
          </div>
        </section>

        <section aria-label="内容原则" className="border-y border-background-200 bg-background-100">
          <div className="mx-auto w-full max-w-[1280px] px-4 py-12 md:px-6 md:py-16">
            <SectionHeading
              eyebrow="内容原则"
              title="四条我们坚持的底线"
              desc="信息可以快，但不可以乱。以下是我们对每一篇内容的基本要求。"
            />
            <div className="mt-8 grid grid-cols-1 gap-4 sm:grid-cols-2">
              {principles.map((p) => (
                <div
                  key={p.title}
                  className="flex gap-4 rounded-lg border border-background-200 bg-background-50 p-5"
                >
                  <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-md bg-primary-100 text-primary-600">
                    <i className={`${p.icon} text-xl`} aria-hidden="true"></i>
                  </span>
                  <div>
                    <h3 className="font-heading text-base font-semibold text-foreground-950">{p.title}</h3>
                    <p className="mt-1.5 text-sm leading-relaxed text-foreground-600">{p.desc}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        <section aria-label="发展理念" className="mx-auto w-full max-w-[1280px] px-4 py-12 md:px-6 md:py-16">
          <div className="grid grid-cols-1 gap-6 md:grid-cols-3">
            {values.map((v) => (
              <div key={v.num} className="rounded-lg bg-secondary-800 p-6 text-background-100">
                <span className="font-heading text-3xl font-bold text-accent-400">{v.num}</span>
                <h3 className="mt-3 font-heading text-lg font-semibold text-background-50">{v.title}</h3>
                <p className="mt-2 text-sm leading-relaxed text-background-300">{v.desc}</p>
              </div>
            ))}
          </div>
        </section>

        <section id="contact" aria-label="联系方式" className="mx-auto w-full max-w-[1280px] px-4 pb-16 md:px-6">
          <div className="grid grid-cols-1 gap-8 rounded-lg border border-background-200 bg-background-50 p-6 md:p-10 lg:grid-cols-2">
            <div>
              <SectionHeading
                eyebrow="联系我们"
                title="有线索、有建议，欢迎来信"
                desc="不管是内容纠错、线索提供，还是转载合作，都可以直接给我们发邮件，我们会在工作日尽快回复。"
              />
              <ul className="mt-6 space-y-4">
                <li className="flex items-center gap-3">
                  <span className="flex h-10 w-10 items-center justify-center rounded-md bg-primary-100 text-primary-600">
                    <i className="ri-mail-line text-lg" aria-hidden="true"></i>
                  </span>
                  <div>
                    <p className="text-xs text-foreground-500">内容与合作邮箱</p>
                    <a
                      href="mailto:hello@kanliao99.com"
                      className="text-sm font-medium text-foreground-900 transition-colors hover:text-primary-600"
                    >
                      hello@kanliao99.com
                    </a>
                  </div>
                </li>
                <li className="flex items-center gap-3">
                  <span className="flex h-10 w-10 items-center justify-center rounded-md bg-secondary-100 text-secondary-800">
                    <i className="ri-time-line text-lg" aria-hidden="true"></i>
                  </span>
                  <div>
                    <p className="text-xs text-foreground-500">内容更新</p>
                    <p className="text-sm font-medium text-foreground-900">工作日每日更新，热点当天补充</p>
                  </div>
                </li>
                <li className="flex items-center gap-3">
                  <span className="flex h-10 w-10 items-center justify-center rounded-md bg-accent-100 text-accent-800">
                    <i className="ri-map-pin-line text-lg" aria-hidden="true"></i>
                  </span>
                  <div>
                    <p className="text-xs text-foreground-500">服务范围</p>
                    <p className="text-sm font-medium text-foreground-900">线上内容服务，面向全国读者</p>
                  </div>
                </li>
              </ul>
            </div>
            <div className="flex flex-col justify-center rounded-lg bg-background-100 p-6">
              <h3 className="font-heading text-lg font-semibold text-foreground-950">订阅更新提醒</h3>
              <p className="mt-2 text-sm leading-relaxed text-foreground-600">
                留下邮箱，重要爆料发布时我们会第一时间通知你。也可以在首页的订阅区完成订阅。
              </p>
              <Link
                to="/#subscribe"
                className="mt-5 inline-flex w-fit items-center gap-1.5 rounded-md bg-primary-500 px-5 py-2.5 text-sm font-medium text-background-50 dark:text-foreground-950 whitespace-nowrap transition-colors hover:bg-primary-600"
              >
                <i className="ri-mail-send-line" aria-hidden="true"></i>
                前往订阅
              </Link>
            </div>
          </div>
        </section>
      </div>
    </SiteLayout>
  );
}